// ─── Location: one-tap geolocation, distance math, remembered preference ───
// The home feed is nearby-first: with a location we sort deals by distance
// and show km chips; without one we fall back to quadrant filters.
// The user's choice (granted/denied) is remembered in localStorage — we
// never nag twice. A re-trigger lives on the profile screen.

const LOC_KEY = "ctd-loc-v1";

export function loadLocPref() {
  try {
    const raw = JSON.parse(localStorage.getItem(LOC_KEY) || "null");
    if (raw && (raw.status === "granted" || raw.status === "denied")) return raw;
  } catch { /* ignore */ }
  return { status: "unasked" };
}

export function saveLocPref(pref) {
  try {
    localStorage.setItem(LOC_KEY, JSON.stringify(pref));
  } catch { /* ignore */ }
}

export function clearLocPref() {
  try {
    localStorage.removeItem(LOC_KEY);
  } catch { /* ignore */ }
}

export const GEO_SUPPORTED =
  typeof navigator !== "undefined" && !!navigator.geolocation;

// Single-shot position request. Resolves { lat, lng } or rejects with a
// short machine-readable reason: "denied" | "unavailable" | "timeout".
export function requestPosition() {
  return new Promise((resolve, reject) => {
    if (!GEO_SUPPORTED) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        if (err && err.code === 1) reject(new Error("denied"));
        else if (err && err.code === 3) reject(new Error("timeout"));
        else reject(new Error("unavailable"));
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  });
}

// Haversine distance in km.
export function haversineKm(aLat, aLng, bLat, bLng) {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const a =
    s1 * s1 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * s2 * s2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// "0.8 km" / "850 m" — tabular numerals via the .tnum class at the call site.
export function formatDistance(km) {
  if (km == null || isNaN(km)) return null;
  if (km < 1) return `${Math.max(50, Math.round(km * 1000))} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

// Distance from the user to a deal, or null when either side lacks coords.
export function dealDistanceKm(deal, loc) {
  if (!loc || deal.lat == null || deal.lng == null) return null;
  return haversineKm(loc.lat, loc.lng, deal.lat, deal.lng);
}
